import { el, clear } from "@utils/dom";
import { nid } from "@utils/id";
import { getState, setState } from "@state/store";
import type { Table, TableShape, Seat, SeatingChart } from "../../types/models";
import { errorToast } from "./Toasts";

// Theme-aware table colors (work well in both light and dark modes)
const TABLE_COLORS_LIGHT = [
    "#e8f4f8", "#f8e8f4", "#f4f8e8", "#f8f4e8",
    "#e8e8f8", "#f8e8e8", "#e8f8e8", "#f4e8f8"
];

const TABLE_COLORS_DARK = [
    "#1a3a4a", "#3a1a3a", "#2a3a1a", "#3a3a1a",
    "#2a2a4a", "#3a2a2a", "#1a3a2a", "#3a1a4a"
];

function getTableColors(): string[] {
    // Check if dark mode is active
    const isDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    return isDark ? TABLE_COLORS_DARK : TABLE_COLORS_LIGHT;
}

/**
 * Show a prompt dialog and return the entered value
 */
function showPromptDialog(message: string, defaultValue: string): Promise<string | null> {
    return new Promise((resolve) => {
        const backdrop = el("div", {
            style: "position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 1000; display: flex; align-items: center; justify-content: center;"
        });

        const dialog = el("div", {
            style: "background: var(--card); border-radius: 16px; padding: 24px; max-width: 400px; width: 90%; border: 1px solid var(--border);"
        });

        const label = el("label", { style: "display: block; margin-bottom: 12px; font-weight: 500;" }, message);

        const input = el("input", {
            type: "text",
            value: defaultValue,
            style: "width: 100%; padding: 10px 12px; border: 1px solid var(--border); border-radius: 8px; background: var(--bg); color: var(--fg); font-size: 14px; box-sizing: border-box;"
        }) as HTMLInputElement;

        const buttonRow = el("div", { style: "display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px;" });

        const cancelBtn = el("button", { style: "padding: 8px 16px;" }, "Cancel");
        const confirmBtn = el("button", { class: "primary", style: "padding: 8px 16px;" }, "Create");

        const close = (value: string | null) => {
            backdrop.remove();
            resolve(value);
        };

        cancelBtn.addEventListener("click", () => close(null));
        confirmBtn.addEventListener("click", () => close(input.value || null));

        input.addEventListener("keydown", (e) => {
            if (e.key === "Enter") close(input.value || null);
            if (e.key === "Escape") close(null);
        });

        backdrop.addEventListener("click", (e) => {
            if (e.target === backdrop) close(null);
        });

        buttonRow.append(cancelBtn, confirmBtn);
        dialog.append(label, input, buttonRow);
        backdrop.appendChild(dialog);
        document.body.appendChild(backdrop);

        input.focus();
        input.select();
    });
}

/**
 * Show a confirm dialog and return true/false
 */
function showConfirmDialog(message: string, confirmText = "Delete"): Promise<boolean> {
    return new Promise((resolve) => {
        const backdrop = el("div", {
            style: "position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 1000; display: flex; align-items: center; justify-content: center;"
        });

        const dialog = el("div", {
            style: "background: var(--card); border-radius: 16px; padding: 24px; max-width: 400px; width: 90%; border: 1px solid var(--border);"
        });

        const label = el("p", { style: "margin: 0 0 16px 0; font-weight: 500;" }, message);

        const buttonRow = el("div", { style: "display: flex; gap: 8px; justify-content: flex-end;" });

        const cancelBtn = el("button", { style: "padding: 8px 16px;" }, "Cancel");
        const confirmBtn = el("button", { class: "danger", style: "padding: 8px 16px;" }, confirmText);

        const close = (result: boolean) => {
            backdrop.remove();
            resolve(result);
        };

        cancelBtn.addEventListener("click", () => close(false));
        confirmBtn.addEventListener("click", () => close(true));

        backdrop.addEventListener("keydown", (e) => {
            if (e.key === "Escape") close(false);
            if (e.key === "Enter") close(true);
        });

        backdrop.addEventListener("click", (e) => {
            if (e.target === backdrop) close(false);
        });

        buttonRow.append(cancelBtn, confirmBtn);
        dialog.append(label, buttonRow);
        backdrop.appendChild(dialog);
        document.body.appendChild(backdrop);

        confirmBtn.focus();
    });
}

/**
 * Snap a point to the edge of a table using raycast approach
 * Casts rays from mouse toward table center to find closest edge hit
 */
function snapToEdge(
    mouseX: number,
    mouseY: number,
    cx: number,
    cy: number,
    width: number,
    height: number,
    shape: 'round' | 'rectangular'
): { x: number; y: number } {
    const padding = 20; // Distance from table edge to seat center

    if (shape === 'round') {
        // For round tables: project mouse onto circle edge
        const radius = Math.min(width, height) / 2;
        const dx = mouseX - cx;
        const dy = mouseY - cy;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < 0.001) {
            return { x: cx + radius + padding, y: cy };
        }

        // Normalize and place on circle edge + padding
        const nx = dx / dist;
        const ny = dy / dist;
        return {
            x: cx + nx * (radius + padding),
            y: cy + ny * (radius + padding)
        };
    } else {
        // For rectangular: cast 360 rays in 140° arc toward table, find closest edge hit
        const numRays = 360;
        let bestPoint = { x: width + padding, y: cy };
        let bestDist = Infinity;

        // Calculate angle from mouse to table center
        const toTableX = cx - mouseX;
        const toTableY = cy - mouseY;
        const centerAngle = Math.atan2(toTableY, toTableX);

        for (let i = 0; i < numRays; i++) {
            // Spread rays across 140° arc (-90° to +90° from center angle)
            const offsetAngle = ((i / (numRays - 1)) - 0.5) * Math.PI;
            const angle = centerAngle + offsetAngle;
            const dirX = Math.cos(angle);
            const dirY = Math.sin(angle);

            // Test intersection with each edge
            const hits: { x: number; y: number; t: number }[] = [];

            // Top edge (y = 0)
            if (Math.abs(dirY) > 0.0001) {
                const t = -mouseY / dirY;
                if (t > 0) {
                    const x = mouseX + t * dirX;
                    if (x >= 0 && x <= width) {
                        hits.push({ x, y: -padding, t });
                    }
                }
            }

            // Bottom edge (y = height)
            if (Math.abs(dirY) > 0.0001) {
                const t = (height - mouseY) / dirY;
                if (t > 0) {
                    const x = mouseX + t * dirX;
                    if (x >= 0 && x <= width) {
                        hits.push({ x, y: height + padding, t });
                    }
                }
            }

            // Left edge (x = 0)
            if (Math.abs(dirX) > 0.0001) {
                const t = -mouseX / dirX;
                if (t > 0) {
                    const y = mouseY + t * dirY;
                    if (y >= 0 && y <= height) {
                        hits.push({ x: -padding, y, t });
                    }
                }
            }

            // Right edge (x = width)
            if (Math.abs(dirX) > 0.0001) {
                const t = (width - mouseX) / dirX;
                if (t > 0) {
                    const y = mouseY + t * dirY;
                    if (y >= 0 && y <= height) {
                        hits.push({ x: width + padding, y, t });
                    }
                }
            }

            // Keep closest hit
            for (const hit of hits) {
                if (hit.t < bestDist) {
                    bestDist = hit.t;
                    bestPoint = { x: hit.x, y: hit.y };
                }
            }
        }

        return bestPoint;
    }
}

/**
 * Open the seating chart editor dialog
 */
export function openSeatingChartEditor(): void {
    const s = getState();
    const cls = s.classes.find(c => c.id === s.activeClassId);
    if (!cls) return;

    // Get or create the active chart
    let chart = cls.seatingCharts?.find(c => c.id === cls.activeChartId);
    if (!chart && cls.seatingCharts && cls.seatingCharts.length > 0) {
        chart = cls.seatingCharts[0];
    }

    // Create modal backdrop
    const backdrop = el("div", {
        style: "position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 1000; display: flex; align-items: center; justify-content: center;"
    });

    // Create dialog
    const dialog = el("div", {
        style: "background: var(--card); border-radius: 16px; padding: 24px; max-width: 400px; width: 95%; max-height: 90vh; overflow: auto; border: 1px solid var(--border);"
    });

    const title = el("h2", { style: "margin: 0 0 16px 0;" }, "Seating Chart Editor");

    // Chart selector or create new
    const chartControls = el("div", { style: "display: flex; gap: 8px; margin-bottom: 16px; align-items: center;" });

    const chartSelect = el("select", { style: "flex: 1;" }) as HTMLSelectElement;

    function populateChartSelect() {
        clear(chartSelect);
        const currentCls = getState().classes.find(c => c.id === s.activeClassId);
        if (!currentCls?.seatingCharts || currentCls.seatingCharts.length === 0) {
            chartSelect.appendChild(el("option", { value: "" }, "No charts - create one"));
            return;
        }

        for (const c of currentCls.seatingCharts) {
            const opt = el("option", { value: c.id }, c.name);
            if (c.id === currentCls.activeChartId) {
                opt.selected = true;
            }
            chartSelect.appendChild(opt);
        }
    }

    populateChartSelect();

    chartSelect.addEventListener("change", () => {
        setState(ns => {
            const c = ns.classes.find(x => x.id === s.activeClassId);
            if (c) {
                const val = chartSelect.value;
                c.activeChartId = val ? val : undefined;
            }
        });
        renderCanvas();
    });

    const newChartBtn = el("button", { class: "primary" }, "+ New Chart");
    newChartBtn.addEventListener("click", async () => {
        const currentState = getState();
        const currentCls = currentState.classes.find(c => c.id === s.activeClassId);
        const defaultName = `Chart ${(currentCls?.seatingCharts?.length ?? 0) + 1}`;

        const name = await showPromptDialog("Chart name:", defaultName);
        if (!name) return;

        const newChart: SeatingChart = {
            id: nid("chart_"),
            name,
            tables: [],
            proximityConstraints: []
        };

        setState(ns => {
            const c = ns.classes.find(x => x.id === s.activeClassId);
            if (c) {
                if (!c.seatingCharts) c.seatingCharts = [];
                c.seatingCharts.push(newChart);
                c.activeChartId = newChart.id;
            }
        });

        populateChartSelect();
        renderCanvas();
    });

    const deleteChartBtn = el("button", { class: "danger" }, "Delete");
    deleteChartBtn.addEventListener("click", async () => {
        const currentState = getState();
        const currentCls = currentState.classes.find(c => c.id === s.activeClassId);
        const currentChart = currentCls?.seatingCharts?.find(c => c.id === currentCls?.activeChartId);
        if (!currentChart) return;

        const confirmed = await showConfirmDialog(`Delete chart "${currentChart.name}"?`);
        if (!confirmed) return;

        setState(ns => {
            const c = ns.classes.find(x => x.id === s.activeClassId);
            if (c?.seatingCharts) {
                c.seatingCharts = c.seatingCharts.filter(ch => ch.id !== currentChart.id);
                const firstChart = c.seatingCharts[0];
                c.activeChartId = firstChart ? firstChart.id : undefined;
            }
        });

        populateChartSelect();
        renderCanvas();
    });

    chartControls.append(chartSelect, newChartBtn, deleteChartBtn);

    // Toolbar with draggable items
    const toolbar = el("div", {
        class: "editor-toolbar",
        style: `
            display: flex;
            gap: 16px;
            padding: 12px 16px;
            background: var(--bg);
            border: 1px solid var(--border);
            border-radius: 8px;
            margin-bottom: 12px;
            align-items: center;
        `
    });

    const toolbarLabel = el("span", {
        style: "font-size: 0.875rem; color: var(--muted); margin-right: 8px;"
    }, "Drag to add:");

    // Round table toolbar item
    const roundTableItem = el("div", {
        class: "toolbar-item",
        draggable: "true",
        "data-type": "round-table",
        style: `
            width: 50px;
            height: 50px;
            border-radius: 50%;
            background: var(--card);
            border: 2px solid var(--border);
            cursor: grab;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 10px;
            color: var(--muted);
            transition: transform 0.15s, box-shadow 0.15s;
        `
    }, "Round");

    roundTableItem.addEventListener("dragstart", (e: DragEvent) => {
        e.dataTransfer?.setData("item-type", "round-table");
        roundTableItem.style.opacity = "0.5";
    });
    roundTableItem.addEventListener("dragend", () => {
        roundTableItem.style.opacity = "1";
    });

    // Rectangular table toolbar item
    const rectTableItem = el("div", {
        class: "toolbar-item",
        draggable: "true",
        "data-type": "rect-table",
        style: `
            width: 70px;
            height: 45px;
            border-radius: 6px;
            background: var(--card);
            border: 2px solid var(--border);
            cursor: grab;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 10px;
            color: var(--muted);
            transition: transform 0.15s, box-shadow 0.15s;
        `
    }, "Rect");

    rectTableItem.addEventListener("dragstart", (e: DragEvent) => {
        e.dataTransfer?.setData("item-type", "rect-table");
        rectTableItem.style.opacity = "0.5";
    });
    rectTableItem.addEventListener("dragend", () => {
        rectTableItem.style.opacity = "1";
    });

    // Chair/seat toolbar item
    const chairItem = el("div", {
        class: "toolbar-item",
        draggable: "true",
        "data-type": "chair",
        style: `
            width: 36px;
            height: 36px;
            border-radius: 50%;
            background: var(--card);
            border: 2px dashed var(--border);
            cursor: grab;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 16px;
            transition: transform 0.15s, box-shadow 0.15s;
        `
    }, "🪑");

    chairItem.addEventListener("dragstart", (e: DragEvent) => {
        e.dataTransfer?.setData("item-type", "chair");
        chairItem.style.opacity = "0.5";
    });
    chairItem.addEventListener("dragend", () => {
        chairItem.style.opacity = "1";
    });

    // Hover effects
    [roundTableItem, rectTableItem, chairItem].forEach(item => {
        item.addEventListener("mouseenter", () => {
            item.style.transform = "scale(1.05)";
            item.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";
        });
        item.addEventListener("mouseleave", () => {
            item.style.transform = "scale(1)";
            item.style.boxShadow = "none";
        });
    });

    toolbar.append(toolbarLabel, roundTableItem, rectTableItem, chairItem);

    // Canvas for table layout
    const canvas = el("div", {
        class: "chart-canvas",
        style: "position: relative; min-height: 600px; background: var(--bg); border: 2px dashed var(--border); border-radius: 12px; overflow: hidden;"
    });

    let draggedTable: string | null = null;
    let dragOffset = { x: 0, y: 0 };

    // Seat dragging state
    let draggedSeat: { tableId: string; seatId: string; seatIndex: number } | null = null;
    let seatDragOffset = { x: 0, y: 0 };

    // Table resizing state
    let resizingTable: { tableId: string; corner: string; startX: number; startY: number; startWidth: number; startHeight: number; startTableX: number; startTableY: number } | null = null;

    // Drop handling for toolbar items on canvas
    canvas.addEventListener("dragover", (e: DragEvent) => {
        e.preventDefault();
        const itemType = e.dataTransfer?.types.includes("item-type");
        if (itemType) {
            canvas.style.borderColor = "var(--primary)";
            canvas.style.background = "var(--bg)";
        }
    });

    canvas.addEventListener("dragleave", () => {
        canvas.style.borderColor = "var(--border)";
    });

    canvas.addEventListener("drop", (e: DragEvent) => {
        e.preventDefault();
        canvas.style.borderColor = "var(--border)";

        const itemType = e.dataTransfer?.getData("item-type");
        if (!itemType) return;

        const currentState = getState();
        const currentCls = currentState.classes.find(c => c.id === s.activeClassId);
        const currentChart = currentCls?.seatingCharts?.find(c => c.id === currentCls?.activeChartId);

        if (!currentChart) {
            errorToast("Create a chart first!");
            return;
        }

        const canvasRect = canvas.getBoundingClientRect();
        const x = e.clientX - canvasRect.left - 50;
        const y = e.clientY - canvasRect.top - 50;

        if (itemType === "round-table" || itemType === "rect-table") {
            const shape: TableShape = itemType === "round-table" ? "round" : "rectangular";

            const newTable: Table = {
                id: nid("tbl_"),
                name: `Table ${currentChart.tables.length + 1}`,
                shape,
                seats: [], // Start with no seats - user will drag chairs
                x: Math.max(0, x),
                y: Math.max(0, y)
            };

            setState(ns => {
                const c = ns.classes.find(x => x.id === s.activeClassId);
                const ch = c?.seatingCharts?.find(x => x.id === currentChart.id);
                if (ch) ch.tables.push(newTable);
            });

            renderCanvas();
        }
    });

    function renderCanvas() {
        clear(canvas);

        const currentState = getState();
        const currentCls = currentState.classes.find(c => c.id === s.activeClassId);
        const currentChart = currentCls?.seatingCharts?.find(c => c.id === currentCls?.activeChartId);

        if (!currentChart) {
            canvas.appendChild(
                el("div", {
                    style: "display: flex; align-items: center; justify-content: center; height: 100%; color: var(--muted);"
                }, "Create a chart to add tables")
            );
            return;
        }

        if (currentChart.tables.length === 0) {
            canvas.appendChild(
                el("div", {
                    style: "display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; color: var(--muted); gap: 8px;"
                },
                    el("span", { style: "font-size: 2rem;" }, "📋"),
                    el("span", {}, "Drag tables from the toolbar above"),
                    el("span", { style: "font-size: 0.875rem;" }, "Then drag chairs onto tables to add seats"),
                    el("span", { style: "font-size: 0.875rem; color: var(--muted);" }, "Right-click a seat to delete it")
                )
            );
            return;
        }

        for (const table of currentChart.tables) {
            const tableEl = renderTable(table, currentChart.id);
            canvas.appendChild(tableEl);
        }
    }

    function renderTable(table: Table, chartId: string): HTMLElement {
        const size = getTableSize(table);
        const tableColors = getTableColors();
        const colorIndex = parseInt(table.id.slice(-2), 36) % tableColors.length;
        const bgColor = tableColors[colorIndex];

        const tableEl = el("div", {
            class: `seating-table ${table.shape}`,
            "data-table-id": table.id,
            style: `
                position: absolute;
                left: ${table.x}px;
                top: ${table.y}px;
                width: ${size.width}px;
                height: ${size.height}px;
                background: ${bgColor};
                border: 2px solid var(--border);
                cursor: move;
            `
        });

        // Clone button
        const cloneBtn = el("button", {
            class: "table-clone-btn",
            style: "position: absolute; top: -8px; left: -8px; width: 20px; height: 20px; border-radius: 50%; background: var(--primary); color: white; border: 2px solid white; font-size: 10px; cursor: pointer; opacity: 0; transition: opacity 0.2s;",
            title: "Clone table",
            onclick: (e: Event) => {
                e.stopPropagation();
                setState(ns => {
                    const c = ns.classes.find(x => x.id === s.activeClassId);
                    const ch = c?.seatingCharts?.find(x => x.id === chartId);
                    if (!ch) return;

                    // Create cloned table with new IDs
                    const clonedTable: Table = {
                        id: nid("tbl_"),
                        name: `${table.name} (copy)`,
                        shape: table.shape,
                        x: table.x + 30,
                        y: table.y + 30,
                        seats: table.seats.map(seat => {
                            const clonedSeat: Seat = {
                                id: nid("seat_"),
                                position: seat.position
                            };
                            if (seat.x !== undefined) clonedSeat.x = seat.x;
                            if (seat.y !== undefined) clonedSeat.y = seat.y;
                            return clonedSeat;
                        })
                    };
                    if (table.width !== undefined) clonedTable.width = table.width;
                    if (table.height !== undefined) clonedTable.height = table.height;

                    ch.tables.push(clonedTable);
                });
                renderCanvas();
            }
        }, "⧉");

        tableEl.appendChild(cloneBtn);

        // Delete button
        const deleteBtn = el("button", {
            class: "table-delete-btn",
            style: "position: absolute; top: -8px; right: -8px; width: 20px; height: 20px; border-radius: 50%; background: var(--danger); color: white; border: 2px solid white; font-size: 12px; cursor: pointer; opacity: 0; transition: opacity 0.2s;",
            onclick: async (e: Event) => {
                e.stopPropagation();
                const confirmed = await showConfirmDialog(`Delete ${table.name}?`);
                if (!confirmed) return;
                setState(ns => {
                    const c = ns.classes.find(x => x.id === s.activeClassId);
                    const ch = c?.seatingCharts?.find(x => x.id === chartId);
                    if (ch) ch.tables = ch.tables.filter(t => t.id !== table.id);
                });
                renderCanvas();
            }
        }, "×");

        tableEl.appendChild(deleteBtn);

        // Show clone and delete on hover - check relatedTarget to prevent flicker
        tableEl.addEventListener("mouseenter", () => {
            cloneBtn.style.opacity = "1";
            deleteBtn.style.opacity = "1";
        });
        tableEl.addEventListener("mouseleave", (e: MouseEvent) => {
            // Don't hide buttons if moving to one of them (they're positioned outside table bounds)
            const related = e.relatedTarget as HTMLElement | null;
            if (related === cloneBtn || related === deleteBtn ||
                cloneBtn.contains(related) || deleteBtn.contains(related)) {
                return;
            }
            cloneBtn.style.opacity = "0";
            deleteBtn.style.opacity = "0";
        });

        // Also hide buttons when leaving the buttons themselves (if not going back to table)
        const hideButtonsOnLeave = (e: MouseEvent) => {
            const related = e.relatedTarget as HTMLElement | null;
            if (related === tableEl || tableEl.contains(related) ||
                related === cloneBtn || related === deleteBtn) {
                return;
            }
            cloneBtn.style.opacity = "0";
            deleteBtn.style.opacity = "0";
        };
        cloneBtn.addEventListener("mouseleave", hideButtonsOnLeave);
        deleteBtn.addEventListener("mouseleave", hideButtonsOnLeave);

        // Chair drop handling
        tableEl.addEventListener("dragover", (e: DragEvent) => {
            const itemType = e.dataTransfer?.types.includes("item-type");
            if (itemType) {
                e.preventDefault();
                e.stopPropagation();
                tableEl.style.boxShadow = "0 0 0 3px var(--primary)";
            }
        });

        tableEl.addEventListener("dragleave", (e: DragEvent) => {
            // Only remove highlight if leaving the table (not entering a child)
            const relatedTarget = e.relatedTarget as HTMLElement;
            if (!tableEl.contains(relatedTarget)) {
                tableEl.style.boxShadow = "none";
            }
        });

        tableEl.addEventListener("drop", (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            tableEl.style.boxShadow = "none";

            const itemType = e.dataTransfer?.getData("item-type");
            if (itemType !== "chair") return;

            // Add a new seat to this table
            const tableRect = tableEl.getBoundingClientRect();
            const dropX = e.clientX - tableRect.left;
            const dropY = e.clientY - tableRect.top;

            const newSeat: Seat = {
                id: nid("seat_"),
                position: table.seats.length,
                x: dropX,
                y: dropY
            };

            setState(ns => {
                const c = ns.classes.find(x => x.id === s.activeClassId);
                const ch = c?.seatingCharts?.find(x => x.id === chartId);
                const t = ch?.tables.find(x => x.id === table.id);
                if (t) t.seats.push(newSeat);
            });

            renderCanvas();
        });

        // Render seats around the table
        renderSeatsForTable(tableEl, table, chartId, size);

        // Resize handles (corners)
        const corners = ['nw', 'ne', 'sw', 'se'];
        for (const corner of corners) {
            const handle = el("div", {
                class: `resize-handle resize-${corner}`,
                "data-corner": corner,
                style: `
                    position: absolute;
                    width: 12px;
                    height: 12px;
                    background: var(--primary);
                    border: 2px solid var(--card);
                    border-radius: 3px;
                    cursor: ${corner}-resize;
                    z-index: 10;
                    ${corner.includes('n') ? 'top: -6px;' : 'bottom: -6px;'}
                    ${corner.includes('w') ? 'left: -6px;' : 'right: -6px;'}
                `
            });

            handle.addEventListener("mousedown", (e: MouseEvent) => {
                e.stopPropagation();
                resizingTable = { tableId: table.id, corner, startX: e.clientX, startY: e.clientY, startWidth: size.width, startHeight: size.height, startTableX: table.x, startTableY: table.y };
            });

            tableEl.appendChild(handle);
        }

        // Drag functionality
        tableEl.addEventListener("mousedown", (e: MouseEvent) => {
            if ((e.target as HTMLElement).tagName === "INPUT") return;
            draggedTable = table.id;
            const rect = tableEl.getBoundingClientRect();
            dragOffset = {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            };
            tableEl.classList.add("dragging");
        });

        return tableEl;
    }

    function renderSeatsForTable(tableEl: HTMLElement, table: Table, chartId: string, size: { width: number; height: number }) {
        const seatPositions = calculateSeatPositions(table.shape, table.seats, size);
        const cx = size.width / 2;
        const cy = size.height / 2;

        for (let i = 0; i < table.seats.length; i++) {
            const seat = table.seats[i];
            const pos = seatPositions[i];

            if (!seat || !pos) continue;

            const seatEl = el("div", {
                class: "seat empty editor-seat",
                "data-seat-id": seat.id,
                "data-seat-index": String(i),
                title: "Drag to reposition • Right-click to delete",
                style: `
                    position: absolute;
                    left: ${pos.x - 18}px;
                    top: ${pos.y - 18}px;
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    background: var(--card);
                    border: 2px dashed var(--border);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 10px;
                    color: var(--muted);
                    cursor: grab;
                `
            }, String(i + 1));

            // Make seat draggable
            seatEl.addEventListener("mousedown", (e: MouseEvent) => {
                e.stopPropagation();
                draggedSeat = { tableId: table.id, seatId: seat.id, seatIndex: i };
                const rect = seatEl.getBoundingClientRect();
                seatDragOffset = {
                    x: e.clientX - rect.left - 18,
                    y: e.clientY - rect.top - 18
                };
                seatEl.style.cursor = "grabbing";
                seatEl.style.zIndex = "100";
            });

            // Right-click to delete seat
            seatEl.addEventListener("contextmenu", (e: MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();

                setState(ns => {
                    const c = ns.classes.find(x => x.id === ns.activeClassId);
                    const ch = c?.seatingCharts?.find(x => x.id === chartId);
                    const t = ch?.tables.find(x => x.id === table.id);
                    if (t) {
                        t.seats = t.seats.filter(s => s.id !== seat.id);
                        // Re-number remaining seats
                        t.seats.forEach((s, idx) => {
                            s.position = idx;
                        });
                    }
                });

                renderCanvas();
            });

            tableEl.appendChild(seatEl);
        }
    }

    // Mouse move handler for dragging tables, seats, and resizing
    canvas.addEventListener("mousemove", (e: MouseEvent) => {
        const canvasRect = canvas.getBoundingClientRect();

        // Handle table dragging
        if (draggedTable) {
            const x = e.clientX - canvasRect.left - dragOffset.x;
            const y = e.clientY - canvasRect.top - dragOffset.y;

            const tableEl = canvas.querySelector(`[data-table-id="${draggedTable}"]`) as HTMLElement;
            if (tableEl) {
                tableEl.style.left = `${Math.max(0, x)}px`;
                tableEl.style.top = `${Math.max(0, y)}px`;
            }
        }

        // Handle seat dragging with edge snapping
        if (draggedSeat) {
            const tableEl = canvas.querySelector(`[data-table-id="${draggedSeat.tableId}"]`) as HTMLElement;
            const seatEl = tableEl?.querySelector(`[data-seat-id="${draggedSeat.seatId}"]`) as HTMLElement;

            if (tableEl && seatEl) {
                const tableRect = tableEl.getBoundingClientRect();
                const tableWidth = parseInt(tableEl.style.width);
                const tableHeight = parseInt(tableEl.style.height);
                const cx = tableWidth / 2;
                const cy = tableHeight / 2;

                // Get mouse position relative to table center
                const mouseX = e.clientX - tableRect.left;
                const mouseY = e.clientY - tableRect.top;

                // Snap to edge based on table shape
                const shape = tableEl.classList.contains('round') ? 'round' : 'rectangular';
                const snapped = snapToEdge(mouseX, mouseY, cx, cy, tableWidth, tableHeight, shape);

                seatEl.style.left = `${snapped.x - 18}px`;
                seatEl.style.top = `${snapped.y - 18}px`;
            }
        }

        // Handle table resizing
        if (resizingTable) {
            const tableEl = canvas.querySelector(`[data-table-id="${resizingTable.tableId}"]`) as HTMLElement;
            if (tableEl) {
                const dx = e.clientX - resizingTable.startX;
                const dy = e.clientY - resizingTable.startY;

                let newWidth = resizingTable.startWidth;
                let newHeight = resizingTable.startHeight;
                let newX = resizingTable.startTableX;
                let newY = resizingTable.startTableY;

                // East side: increase width to the right
                if (resizingTable.corner.includes('e')) {
                    newWidth = Math.max(40, resizingTable.startWidth + dx);
                }
                // West side: increase width to the left, move table left
                if (resizingTable.corner.includes('w')) {
                    const widthChange = Math.min(dx, resizingTable.startWidth - 40);
                    newWidth = resizingTable.startWidth - widthChange;
                    newX = resizingTable.startTableX + widthChange;
                }

                // South side: increase height downward
                if (resizingTable.corner.includes('s')) {
                    newHeight = Math.max(40, resizingTable.startHeight + dy);
                }
                // North side: increase height upward, move table up
                if (resizingTable.corner.includes('n')) {
                    const heightChange = Math.min(dy, resizingTable.startHeight - 40);
                    newHeight = resizingTable.startHeight - heightChange;
                    newY = resizingTable.startTableY + heightChange;
                }

                tableEl.style.width = `${newWidth}px`;
                tableEl.style.height = `${newHeight}px`;
                tableEl.style.left = `${Math.max(0, newX)}px`;
                tableEl.style.top = `${Math.max(0, newY)}px`;
            }
        }
    });

    // Mouse up handler
    const handleMouseUp = () => {
        // Handle table drag end
        if (draggedTable) {
            const tableEl = canvas.querySelector(`[data-table-id="${draggedTable}"]`) as HTMLElement;
            if (tableEl) {
                const x = parseInt(tableEl.style.left);
                const y = parseInt(tableEl.style.top);

                setState(ns => {
                    const c = ns.classes.find(x => x.id === s.activeClassId);
                    const ch = c?.seatingCharts?.find(x => x.id === c?.activeChartId);
                    const t = ch?.tables.find(x => x.id === draggedTable);
                    if (t) {
                        t.x = x;
                        t.y = y;
                    }
                });

                tableEl.classList.remove("dragging");
            }
            draggedTable = null;
        }

        // Handle seat drag end - save snapped position
        if (draggedSeat) {
            const tableEl = canvas.querySelector(`[data-table-id="${draggedSeat.tableId}"]`) as HTMLElement;
            const seatEl = tableEl?.querySelector(`[data-seat-id="${draggedSeat.seatId}"]`) as HTMLElement;

            if (tableEl && seatEl) {
                const seatX = parseInt(seatEl.style.left) + 18;
                const seatY = parseInt(seatEl.style.top) + 18;

                // Get table size to calculate center
                const tableWidth = parseInt(tableEl.style.width);
                const tableHeight = parseInt(tableEl.style.height);
                const cx = tableWidth / 2;
                const cy = tableHeight / 2;

                // Store as offset from center (already snapped during drag)
                const offsetX = seatX - cx;
                const offsetY = seatY - cy;

                setState(ns => {
                    const c = ns.classes.find(x => x.id === s.activeClassId);
                    const ch = c?.seatingCharts?.find(x => x.id === c?.activeChartId);
                    const t = ch?.tables.find(x => x.id === draggedSeat?.tableId);
                    const seat = t?.seats.find(st => st.id === draggedSeat?.seatId);
                    if (seat) {
                        seat.x = offsetX;
                        seat.y = offsetY;
                    }
                });

                seatEl.style.cursor = "grab";
                seatEl.style.zIndex = "";
            }
            draggedSeat = null;
        }

        // Handle resize end - save custom size and position
        if (resizingTable) {
            const tableEl = canvas.querySelector(`[data-table-id="${resizingTable.tableId}"]`) as HTMLElement;
            if (tableEl) {
                const newWidth = parseInt(tableEl.style.width);
                const newHeight = parseInt(tableEl.style.height);
                const newX = parseInt(tableEl.style.left);
                const newY = parseInt(tableEl.style.top);

                setState(ns => {
                    const c = ns.classes.find(x => x.id === s.activeClassId);
                    const ch = c?.seatingCharts?.find(x => x.id === c?.activeChartId);
                    const t = ch?.tables.find(x => x.id === resizingTable?.tableId);
                    if (t) {
                        t.width = newWidth;
                        t.height = newHeight;
                        t.x = newX;
                        t.y = newY;
                    }
                });

                // Re-render to update seat positions based on new size
                renderCanvas();
            }
            resizingTable = null;
        }
    };

    canvas.addEventListener("mouseup", handleMouseUp);
    canvas.addEventListener("mouseleave", handleMouseUp);

    // Close button
    const closeBtn = el("button", { class: "primary", style: "width: 100%; margin-top: 16px;" }, "Done");
    closeBtn.addEventListener("click", () => {
        document.body.removeChild(backdrop);
        document.dispatchEvent(new CustomEvent("seating:update"));
    });

    dialog.append(
        title,
        chartControls,
        toolbar,
        canvas,
        closeBtn
    );

    backdrop.appendChild(dialog);
    document.body.appendChild(backdrop);

    // Close on backdrop click
    backdrop.addEventListener("click", (e) => {
        if (e.target === backdrop) {
            document.body.removeChild(backdrop);
            document.dispatchEvent(new CustomEvent("seating:update"));
        }
    });

    // Initial render
    renderCanvas();
}

function getTableSize(table: Table): { width: number; height: number } {
    // Use custom size if defined
    if (table.width !== undefined && table.height !== undefined) {
        return { width: table.width, height: table.height };
    }

    const seatCount = table.seats.length;
    const baseSize = Math.max(100, 40 + seatCount * 15);

    switch (table.shape) {
        case 'round':
            return { width: baseSize, height: baseSize };
        case 'rectangular':
            return { width: baseSize * 1.5, height: baseSize * 0.8 };
        default:
            return { width: baseSize, height: baseSize };
    }
}

/** Export for use in SeatingChartView */
export { getTableSize };

function calculateSeatPositions(
    shape: TableShape,
    seats: Seat[],
    size: { width: number; height: number }
): { x: number; y: number }[] {
    const positions: { x: number; y: number }[] = [];
    const cx = size.width / 2;
    const cy = size.height / 2;
    const seatCount = seats.length;

    for (let i = 0; i < seatCount; i++) {
        const seat = seats[i];

        // Use custom position if defined
        if (seat && seat.x !== undefined && seat.y !== undefined) {
            // Custom position is stored as offset from table center
            positions.push({
                x: cx + seat.x,
                y: cy + seat.y
            });
            continue;
        }

        // Calculate default position based on shape
        if (shape === 'round') {
            const radius = Math.min(cx, cy) + 20;
            const angle = (i / seatCount) * Math.PI * 2 - Math.PI / 2;
            positions.push({
                x: cx + Math.cos(angle) * radius,
                y: cy + Math.sin(angle) * radius
            });
        } else if (shape === 'rectangular') {
            const padding = 25;
            const perimeter = (size.width + size.height) * 2;
            const spacing = perimeter / seatCount;
            const dist = i * spacing;
            let x: number, y: number;

            if (dist < size.width) {
                x = dist;
                y = -padding;
            } else if (dist < size.width + size.height) {
                x = size.width + padding;
                y = dist - size.width;
            } else if (dist < size.width * 2 + size.height) {
                x = size.width - (dist - size.width - size.height);
                y = size.height + padding;
            } else {
                x = -padding;
                y = size.height - (dist - size.width * 2 - size.height);
            }

            positions.push({ x, y });
        }
    }

    return positions;
}

/** Export for use in SeatingChartView */
export { calculateSeatPositions };

/**
 * Get or create the active seating chart for a class
 */
export function getActiveChart(): SeatingChart | null {
    const s = getState();
    const cls = s.classes.find(c => c.id === s.activeClassId);
    if (!cls) return null;

    if (!cls.seatingCharts || cls.seatingCharts.length === 0) return null;

    const chart = cls.seatingCharts.find(c => c.id === cls.activeChartId);
    return chart ?? cls.seatingCharts[0] ?? null;
}
